import findStep from "./findStep";
import useBotControlStore from "../../../storeModules/useBotControlStore";
import useUserStore from "../../../storeModules/useUserStore";
import { validateCoupon, getProposta } from "../../../api/requests";
import {
  setLoadingMessage,
  removeLoadingMessage,
} from "../../../utils/setLoadingMessage";
import sleepAwait from "../../../utils/sleepAwait";
import useInitialConfigStore from "../../../storeModules/useInitialConfigStore";
import addMoneyMask from "../../../utils/addMoneyMask";
import { metodosPagamentoEnum, couponErrors } from "../../../utils/constants";
import hasUrlCoupon from "../../../utils/hasUrlCoupon";
import sendTag from "../../../utils/sendTag";
import capitalizeFirstCharOfSegments from "../../../utils/capitalizeFirstCharOfSegments";

const eventTagCouponNotUsed = () => {
  const { userCupom } = useUserStore.getState();

  sendTag({
    event: "Cupom Nao Utilizado",
    cupom: userCupom ? userCupom : undefined,
  });
};

const eventTagCouponDontWant = () => {
  const { userCupom } = useUserStore.getState();

  sendTag({
    event: "Cupom Nao Quero Usar",
    cupom: userCupom ? userCupom : undefined,
  });
};

const handleValidateCoupon = async () => {
  const { fakeLoad } = useInitialConfigStore.getState();
  const { userCupom } = useUserStore.getState();

  if (userCupom) {
    setLoadingMessage();
    await sleepAwait(fakeLoad);

    validateCoupon()
      .then(async (response) => {
        const cupomData = response.data.value;
        const userStoreData = useUserStore.getState();

        useUserStore.setState({
          ...userStoreData,
          cupomData: cupomData,
        });

        useBotControlStore.setState({
          currentStep: findStep("cupom-apply-success").index,
        });
      })
      .catch((error) => {
        const errorMessages = error.response.data.validationErrors
          .flat()
          .map((error) => {
            return error.code;
          });

        useUserStore.setState({
          userCupom: "",
        });

        useBotControlStore.setState({
          couponErrorMessages: errorMessages,
        });

        useBotControlStore.setState({
          currentStep: findStep("cupom-error-message-1").index,
        });
      })
      .finally(() => {
        removeLoadingMessage();
      });
  }
};

const clearStoreCoupon = () => {
  const { userStoreData } = useUserStore.getState();

  useUserStore.setState({
    ...userStoreData,
    userCupom: "",
    cupomData: {
      valorDescontado: 0,
      valorTotal: 0,
      valorDesconto: 0,
      tipoDesconto: 0,
      cupomId: 0,
      formasPagamentosValidas: [],
    },
  });
};

const cupomSteps = [
  {
    name: "cupom-initial-message",
    sender: "bot",
    text: () => {
      return `Você tem um <b>cupom de desconto</b> para aplicar?`;
    },
    action: () => {
      sendTag({
        event: "Cupom Aplicar",
      });
    },
    options: [
      {
        text: "SIM, QUERO APLICAR",
        action: async () => {
          useBotControlStore.setState({
            currentStep: findStep("cupom-input").index,
          });

          sendTag({
            event: "Cupom Adicionar",
          });
        },
      },
      {
        text: "NÃO TENHO CUPOM",
        action: async () => {
          eventTagCouponNotUsed();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
  {
    name: "cupom-input",
    sender: "bot",
    inputType: "userCupom",
    text: () => "Digite seu cupom de desconto.",
    action: () => {
      handleValidateCoupon();
    },
    options: [
      {
        text: "NÃO TENHO CUPOM",
        action: async () => {
          eventTagCouponNotUsed();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
  {
    name: "cupom-error-message-1",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const { couponErrorMessages } = useBotControlStore.getState();

      const couponErrorTranslation = couponErrorMessages.map((error) => {
        return couponErrors[error] || "O cupom não pode ser aplicado.";
      });

      const couponErrorMessage = couponErrorTranslation
        .map((message) => `• ${message}<br>`)
        .join(" ");

      return `
              Cupom inválido.
              <br>
              <ul class="coupon-list-error-message">
                ${couponErrorMessage}
              </ul>
            `;
    },
    action: () => {
      const { userCupom } = useUserStore.getState();
      const { couponErrorMessages } = useBotControlStore.getState();

      const couponErrorTranslation = couponErrorMessages.map((error) => {
        return couponErrors[error] || "O cupom não pode ser aplicado.";
      });

      const couponErrorMessageToSendTag = capitalizeFirstCharOfSegments(
        couponErrorTranslation
          .join(" ")
          .replace(/\./g, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
      ).replaceAll(" ", "");

      sendTag({
        event: "Cupom Invalido",
        cupom: userCupom ? userCupom : "",
        msg_erro: couponErrorMessageToSendTag
          ? couponErrorMessageToSendTag
          : "OCupomNaoPodeSerAplicado",
      });

      useBotControlStore.setState({
        currentStep: findStep("cupom-error-message-2").index,
      });
    },
  },
  {
    name: "cupom-error-message-2",
    sender: "bot",
    isWaitingForFetch: true,
    text: "O que você gostaria de fazer?",
    options: [
      {
        text: "APLICAR UM NOVO CUPOM",
        action: async () => {
          useBotControlStore.setState({
            currentStep: findStep("cupom-try-again").index,
          });

          sendTag({
            event: "Cupom Adicionar",
          });
        },
      },
      {
        text: "CONTINUAR SEM CUPOM",
        action: async () => {
          eventTagCouponNotUsed();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
  {
    name: "cupom-try-again",
    sender: "bot",
    inputType: "userCupom",
    text: () => {
      return `Adicione um <b>novo cupom de desconto</b>.`;
    },
    action: () => {
      handleValidateCoupon();
    },
    options: [
      {
        text: "NÃO, CONTINUAR SEM CUPOM",
        action: async () => {
          eventTagCouponNotUsed();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
  {
    name: "cupom-apply-success",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const { cupomData } = useUserStore.getState();
      const discountMessage =
        cupomData.tipoDesconto === 1
          ? `${addMoneyMask(cupomData.valorDesconto)}`
          : `${cupomData.valorDesconto}%`;

      return `Cupom aplicado com sucesso! Você ganhou <b>${discountMessage} de desconto</b> na primeira parcela.`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("cupom-apply-new-value").index,
      });
    },
  },
  {
    name: "cupom-apply-new-value",
    sender: "bot",
    text: () => {
      const { cupomData } = useUserStore.getState();

      return `E o novo valor da primeira parcela é <b>${addMoneyMask(
        cupomData.valorDescontado
      )}</b>`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("cupom-payment-message").index,
      });
    },
  },
  {
    name: "cupom-payment-message",
    sender: "bot",
    text: () => {
      const { cupomData } = useUserStore.getState();
      const paymentMethods = cupomData.formasPagamentosValidas.map(
        (paymentMethod) => {
          return `<b>${metodosPagamentoEnum[paymentMethod]}</b>`;
        }
      );
      return cupomData.formasPagamentosValidas.length < 3
        ? `Lembre-se: os cupons são válidos apenas para pagamentos via ${paymentMethods.join(
            ", "
          )}`
        : null;
    },
    action: () => {
      const codigoCupom = useUserStore.getState().userCupom;

      sendTag({
        event: "Cupom Confirmar",
        cupom: codigoCupom, //Código do cupom
      });
    },
    options: [
      {
        text: "CONFIRMAR",
        action: async () => {
          const { userCupom, cupomData } = useUserStore.getState();
          const codigoCupom = useUserStore.getState().userCupom;
          const { valorDescontado } = useUserStore.getState().cupomData;

          if (userCupom && cupomData.cupomId) {
            getProposta(userCupom);
          }

          window.planos_bot.plano_selecionado.valor_primeira_parcela =
            valorDescontado;

          sendTag({
            event: "Cupom Aplicado Sucesso",
            cupom: codigoCupom, //Código do cupom
          });
        },
      },
      {
        text: "NÃO QUERO USAR O CUPOM",
        action: async () => {
          eventTagCouponDontWant();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
  {
    name: "cupom-initial-url",
    sender: "bot",
    text: () => {
      return `Você tem um cupom disponível.`;
    },
    action: () => {
      const userStoreData = useUserStore.getState();

      useUserStore.setState({
        ...userStoreData,
        userCupom: !!hasUrlCoupon() ? hasUrlCoupon() : "",
      });

      useBotControlStore.setState({
        currentStep: findStep("cupom-apply-url").index,
      });
    },
  },
  {
    name: "cupom-apply-url",
    sender: "bot",
    text: () => {
      const urlCoupon = hasUrlCoupon();
      return `Deseja aplicar o cupom: <b>${urlCoupon}</b>?`;
    },
    action: () => {
      const codigoCupom = useUserStore.getState().userCupom;

      sendTag({
        event: "Cupom Aplicar",
        cupom: codigoCupom, //Código do cupom
      });
    },
    options: [
      {
        text: "SIM, QUERO APLICAR",
        action: async () => {
          handleValidateCoupon();
        },
      },
      {
        text: "NÃO QUERO USAR O CUPOM",
        action: async () => {
          eventTagCouponDontWant();
          clearStoreCoupon();
          getProposta();
        },
      },
    ],
  },
];

export default cupomSteps;
